"use client";

import { ClipboardEvent, SyntheticEvent, useRef, useState } from "react";
import * as Form from "@radix-ui/react-form";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { createBrowserClient } from "@supabase/ssr";
import { SUPABASE_ANON_KEY, SUPABASE_URL } from "@utils/constants";
import LoadingSpinner from "@components/loadingSpinner";
import { analytics } from "@components/analytics";
import { useRouter, useSearchParams } from "next/navigation";
import { Text, Badge, Button } from "@components/theming";
import { useTranslations } from "next-intl";
import { TextInput } from "@components/inputs";
import { useThemeContext } from "@utils/hooks";

interface Props {
  email: string;
  submitCallback?: () => Promise<void>;
  next?: string;
  redirectHardNav?: boolean;
}

export default function CodeInput({
  email,
  submitCallback,
  next,
  redirectHardNav,
}: Props) {
  const eventTranslations = useTranslations("SignIn");

  const router = useRouter();
  const supabase = createBrowserClient(SUPABASE_URL!, SUPABASE_ANON_KEY!);
  const theme = useThemeContext();
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get("redirectTo");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const input1 = useRef<HTMLInputElement>(null);
  const input2 = useRef<HTMLInputElement>(null);
  const input3 = useRef<HTMLInputElement>(null);
  const input4 = useRef<HTMLInputElement>(null);
  const input5 = useRef<HTMLInputElement>(null);
  const input6 = useRef<HTMLInputElement>(null);
  const otherParams = searchParams
    .toString()
    .split("&")
    .filter(
      (param) => !param.startsWith("redirectTo") && !param.startsWith("country")
    )
    .join("&");

  async function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    const { input1, input2, input3, input4, input5, input6 } =
      Object.fromEntries(new FormData(event.currentTarget));
    const code_value = `${input1}${input2}${input3}${input4}${input5}${input6}`;

    // See SignInForm's equivalent flag for why this is set before the
    // actual sign-in call - verifyOtp succeeding fires onAuthStateChange
    // for every subscriber immediately, including SignInPage's own
    // "already signed in, redirect away" effect, which can otherwise win
    // the race against this function's own redirect further down.
    window.sessionStorage.setItem("signInSubmitInProgress", "1");

    const { data, error } = await supabase.auth.verifyOtp({
      token: code_value,
      type: "email",
      email,
    });

    if (error) {
      window.sessionStorage.removeItem("signInSubmitInProgress");
      setLoading(false);
      setError(true);
      return;
    }

    if (!error && !data.user?.app_metadata["iticket_user_uid"]) {
      await supabase.auth.signOut();
      analytics.reset();
      window.sessionStorage.removeItem("signInSubmitInProgress");
      setLoading(false);
      setError(true);
      return;
    }

    if (data.user) {
      analytics.identify(
        data.user.app_metadata?.iticket_user_uid?.toLowerCase(),
        {
          id: data.user.app_metadata?.iticket_user_uid?.toLowerCase(),
          email: data.user.email,
        }
      );

      analytics.track("Sign in", {
        iticket_uid: data.user.app_metadata?.iticket_user_uid?.toLowerCase(),
        email: email,
        method: "otp",
      });
    }

    if (submitCallback) {
      await submitCallback();
    }

    if (redirectTo) {
      if (redirectHardNav) {
        // See SignInForm's equivalent block for why this is set
        // unconditionally rather than only when a patch actually ran.
        window.sessionStorage.setItem("pendingPostcodeSync", "1");
        window.sessionStorage.setItem(
          "pendingPostcodeSyncStartedAt",
          String(Date.now())
        );
        // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- deliberate hard navigation, see SignInForm's redirectHardNav prop doc
        window.location.href = `${redirectTo}?${otherParams}`;
        return;
      }
      window.sessionStorage.removeItem("signInSubmitInProgress");
      router.replace(`${redirectTo}?${otherParams}`);
    } else if (next) {
      window.sessionStorage.removeItem("signInSubmitInProgress");
      router.push(next);
    } else {
      window.sessionStorage.removeItem("signInSubmitInProgress");
    }
    router.refresh();
  }

  const onPaste = (event: ClipboardEvent) => {
    event.preventDefault();
    const code = event.clipboardData?.getData("text");
    if (
      code &&
      input1.current &&
      input2.current &&
      input3.current &&
      input4.current &&
      input5.current &&
      input6.current
    ) {
      input1.current.value = code[0];
      input2.current.value = code[1];
      input3.current.value = code[2];
      input4.current.value = code[3];
      input5.current.value = code[4];
      input6.current.value = code[5];
      input6.current.focus();
    }
  };

  const styledInput = (
    <TextInput
      variant="surface"
      size="3"
      sharedClasses="w-[calc((100%-40px)/6)] max-w-[3rem] aspect-square text-center line-[28px] font-black shrink"
      shopsiteClasses="bg-gray-200 !rounded text-xl focus-visible:outline-none focus-visible:ring focus-visible:ring-indigo-400 border-0 focus:!border-0 !px-0 !py-0 focus:!py-0 focus:!px-0"
      whitelabelClasses="[&>input]:indent-0"
    />
  );

  return (
    <Form.Root
      onSubmit={handleSubmit}
      onClearServerErrors={() => setError(false)}
      className="flex flex-col gap-8"
    >
      <Form.Field name="code" className="flex flex-col gap-4 items-center">
        <div className="flex items-center gap-2">
          <Form.Control
            ref={input1}
            type="text"
            name="input1"
            inputMode="numeric"
            maxLength={1}
            required
            onFocus={(e) => {
              e.target.select();
            }}
            onChange={(e) => {
              setError(false);
              if (
                e.target.value.length === e.target.maxLength &&
                input2.current
              ) {
                input2.current.focus();
              }
            }}
            onPaste={onPaste}
            asChild
          >
            {styledInput}
          </Form.Control>
          <Form.Control
            ref={input2}
            type="text"
            name="input2"
            inputMode="numeric"
            maxLength={1}
            required
            onFocus={(e) => {
              e.target.select();
            }}
            onChange={(e) => {
              setError(false);
              if (
                e.target.value.length === e.target.maxLength &&
                input3.current
              ) {
                input3.current.focus();
              }
            }}
            onKeyDown={(e) => {
              if (
                e.key === "Backspace" &&
                e.currentTarget.value.length === 0 &&
                input1.current
              ) {
                e.preventDefault();
                input1.current.focus();
              }
            }}
            onPaste={onPaste}
            asChild
          >
            {styledInput}
          </Form.Control>
          <Form.Control
            ref={input3}
            type="text"
            name="input3"
            inputMode="numeric"
            maxLength={1}
            required
            onFocus={(e) => {
              e.target.select();
            }}
            onChange={(e) => {
              setError(false);
              if (
                e.target.value.length === e.target.maxLength &&
                input4.current
              ) {
                input4.current.focus();
              }
            }}
            onKeyDown={(e) => {
              if (
                e.key === "Backspace" &&
                e.currentTarget.value.length === 0 &&
                input2.current
              ) {
                e.preventDefault();
                input2.current.focus();
              }
            }}
            onPaste={onPaste}
            asChild
          >
            {styledInput}
          </Form.Control>
          <Form.Control
            ref={input4}
            type="text"
            name="input4"
            inputMode="numeric"
            maxLength={1}
            required
            onFocus={(e) => {
              e.target.select();
            }}
            onChange={(e) => {
              setError(false);
              if (
                e.target.value.length === e.target.maxLength &&
                input5.current
              ) {
                input5.current.focus();
              }
            }}
            onKeyDown={(e) => {
              if (
                e.key === "Backspace" &&
                e.currentTarget.value.length === 0 &&
                input3.current
              ) {
                e.preventDefault();
                input3.current.focus();
              }
            }}
            onPaste={onPaste}
            asChild
          >
            {styledInput}
          </Form.Control>
          <Form.Control
            ref={input5}
            type="text"
            name="input5"
            inputMode="numeric"
            maxLength={1}
            required
            onFocus={(e) => {
              e.target.select();
            }}
            onChange={(e) => {
              setError(false);
              if (
                e.target.value.length === e.target.maxLength &&
                input6.current
              ) {
                input6.current.focus();
              }
            }}
            onKeyDown={(e) => {
              if (
                e.key === "Backspace" &&
                e.currentTarget.value.length === 0 &&
                input4.current
              ) {
                e.preventDefault();
                input4.current.focus();
              }
            }}
            onPaste={onPaste}
            asChild
          >
            {styledInput}
          </Form.Control>
          <Form.Control
            ref={input6}
            type="text"
            name="input6"
            inputMode="numeric"
            maxLength={1}
            required
            onChange={() => {
              setError(false);
            }}
            onFocus={(e) => {
              e.target.select();
            }}
            onKeyDown={(e) => {
              if (
                e.key === "Backspace" &&
                e.currentTarget.value.length === 0 &&
                input5.current
              ) {
                e.preventDefault();
                input5.current.focus();
              }
            }}
            onPaste={onPaste}
            asChild
          >
            {styledInput}
          </Form.Control>
        </div>
        {error && (
          <Badge
            color="red"
            radius={theme && theme.radius === "none" ? "none" : "full"}
          >
            <ExclamationCircleIcon className="h-4 w-4 stroke-2" />
            <Text as="span" size="2">
              {eventTranslations("codeexpired")}
            </Text>
          </Badge>
        )}
      </Form.Field>
      <Form.Submit disabled={loading} asChild>
        <Button size="3">
          {loading ? (
            <div className="flex justify-center items-center gap-2">
              <LoadingSpinner size="18" color="#ffffff" />
              <span>{eventTranslations("signingin")}</span>
            </div>
          ) : (
            <Text as="span" weight="medium">
              {eventTranslations("signin")}
            </Text>
          )}
        </Button>
      </Form.Submit>
    </Form.Root>
  );
}
